import { captureException } from '@sentry/nextjs';
import { AxiosError } from 'axios';

import {
    TResponseAndRequest,
    TErrorData,
    TReadParamsProperties,
    QuestionnairesServiceV2,
    axiosClientSCClass,
    getHeaderAcceptLanguage,
    TResponsePayload,
    TStatusCode,
} from '@/shared/api';
import { ResourceService } from '@/shared/api/base-services/resource-service';
import {
    ECreditProductType,
    NOT_FOUND_PAGE,
    PLATFORM_CODE,
    PLATFORM_ID,
    PRODUCT_TYPE,
    PROJECT_SCENARIO,
    SERVER_ERROR,
    STATUS_CODE_OK,
} from '@/shared/constants/settings';

import {
    TBankCardRepayResp,
    TCustomerBankRequisites,
    TBankCardRepayParams,
    TRequisitesQuestionnaireParamsReq,
    TResponseBankCardStatus,
    TResponseCreationCardUrl,
} from './types';

export const RequisitesService = (context?: TResponseAndRequest) =>
    ResourceService('v2/requisites', axiosClientSCClass, context);

export const getCustomerBankRequisites = async (
    context?: TResponseAndRequest,
): Promise<{ data?: TCustomerBankRequisites } & TStatusCode> => {
    let statusCode = STATUS_CODE_OK;
    try {
        const response = await RequisitesService(context).read<
            TResponsePayload<TCustomerBankRequisites>,
            TReadParamsProperties
        >({
            params: {
                platformCode: ECreditProductType.SITE,
                product: PRODUCT_TYPE,
            },
        });

        return { data: response.payload, statusCode };
    } catch (e) {
        const error = e as AxiosError;
        captureException(error);

        statusCode = error.response?.status === NOT_FOUND_PAGE ? NOT_FOUND_PAGE : SERVER_ERROR;

        return {
            statusCode,
        };
    }
};

export const addBankCard = async (path: string): Promise<TResponseCreationCardUrl & { errorMessage: string }> => {
    try {
        const response = await RequisitesService().command<
            { payload: TResponseCreationCardUrl },
            TReadParamsProperties
        >({
            command: 'cards',
            params: {
                backUrl: path,
                product: PRODUCT_TYPE,
            },
        });

        return { url: response.payload.url, errorMessage: '' };
    } catch (e) {
        const error = e as AxiosError;
        const data = error.response?.data as TErrorData;
        captureException(error);

        return {
            url: '',
            errorMessage: data?.message,
        };
    }
};

export const deleteBankCard = async (id: number): Promise<TStatusCode & { errorMessage?: string }> => {
    try {
        await RequisitesService().delete({
            identifier: `cards/${id}`,
        });

        return { statusCode: STATUS_CODE_OK };
    } catch (e) {
        const error = e as AxiosError;
        const data = error.response?.data as TErrorData;
        captureException(error);

        return {
            statusCode: error.response?.status ?? SERVER_ERROR,
            errorMessage: data?.message,
        };
    }
};

export const getQuestionnaireRequisites = async (
    params: TRequisitesQuestionnaireParamsReq,
): Promise<{ data?: TCustomerBankRequisites } & TStatusCode> => {
    const { number, type } = params;

    try {
        const response = await QuestionnairesServiceV2.readOne<
            TResponsePayload<TCustomerBankRequisites>,
            TReadParamsProperties
        >({
            identifier: `${number}/requisites`,
            params: {
                platformCode: PLATFORM_CODE,
                operationType: type,
            },
        });

        return { data: response.payload, statusCode: STATUS_CODE_OK };
    } catch (e) {
        const error = e as AxiosError;
        const statusCode = error?.response?.status ?? SERVER_ERROR;
        captureException(error);

        return {
            statusCode,
        };
    }
};

export const getBankCardStatus = async (
    id: number,
    locale: string,
): Promise<{ data?: TResponseBankCardStatus } & TStatusCode> => {
    try {
        const response = await RequisitesService().readOne<
            TResponsePayload<TResponseBankCardStatus>,
            TReadParamsProperties
        >({
            identifier: `cards/${id}/status`,
            headers: getHeaderAcceptLanguage(locale),
        });

        return { data: response.payload, statusCode: STATUS_CODE_OK };
    } catch (e) {
        const error = e as AxiosError;
        captureException(error);
        const statusCode = error?.response?.status ?? SERVER_ERROR;

        return { statusCode };
    }
};

export const getBankCardRepay = async (params: TBankCardRepayParams): Promise<{ message: string } & TStatusCode> => {
    const { cardId, number, term, operationType, amount } = params;
    try {
        const response = await RequisitesService().command<TResponsePayload<TBankCardRepayResp>, TReadParamsProperties>(
            {
                command: `cards/${cardId}/repay`,
                params: {
                    platformId: PLATFORM_ID,
                    operationType,
                    scenario: PROJECT_SCENARIO,
                },
                data: {
                    number,
                    amount,
                    ...(term && { term }),
                },
            },
        );

        return {
            statusCode: STATUS_CODE_OK,
            message: response.payload.message,
        };
    } catch (e) {
        const error = e as AxiosError;
        const data = error.response?.data as TErrorData;
        captureException(error);

        const statusCode = error?.response?.status === SERVER_ERROR ? SERVER_ERROR : NOT_FOUND_PAGE;

        return {
            statusCode,
            message: data?.message ?? 'Ошибка загрузки данных',
        };
    }
};
